import { useQuery } from '@tanstack/react-query';
import { UsersService, useGetIdentityLocationCounts, usersKeys } from '../../Users';
import { IdentityAdapters } from '../adapters';
import { identitiesKeys } from '../queryKeys';
import { IdentitiesService } from '../services';

const READ_OPTIONS = {
  retry: false,
  refetchOnWindowFocus: false,
  // Neither read toasts. A failed identities read shows the tab's own error
  // copy, which a global toast would only repeat. A failed users read shows
  // nothing but em dashes in Phone and Last Active: the approved behaviour,
  // which (unlike the counts' toast) no sibling page settled otherwise.
  meta: { skipGlobalErrorToaster: true },
};

/**
 * The office profile's Location Admins (GOTU-7483): everyone
 * organization-service says has access to the office's location, A–Z, with
 * phone numbers and last activity from auth-service and each identity's
 * location count from organization-service.
 *
 * Disabled until the profile has resolved the office id, which callers must
 * treat as loading rather than as an office nobody can access.
 *
 * The users and the counts are later reads, keyed on the identity ids. Either
 * failing keeps the rows and leaves its cells an em dash: who has access is the
 * point of the tab, and is not worth hiding over a secondary column. While the
 * users load the list stays pending, so the table does not flash em dashes for
 * numbers that are about to arrive. The counts never hold the list: their cell
 * stays blank until they land, and a failure also toasts, as it does on the
 * Location Users list that shows the same count.
 */
export const useLocationAdmins = (officeId) => {
  const identitiesQuery = useQuery({
    queryKey: identitiesKeys.locationIdentities(officeId),
    queryFn: () => IdentitiesService.getLocationIdentities(officeId),
    enabled: Boolean(officeId),
    ...READ_OPTIONS,
  });

  const identities = identitiesQuery.data ?? [];
  const identityIds = identities.map((identity) => identity.identityId);

  const usersQuery = useQuery({
    queryKey: usersKeys.byIds(identityIds),
    queryFn: () => UsersService.getUsersByIds(identityIds),
    enabled: identityIds.length > 0,
    ...READ_OPTIONS,
  });

  const { countsById, isError: isCountsError } = useGetIdentityLocationCounts(identityIds);

  return {
    admins: IdentityAdapters.toLocationAdminRows(
      identities,
      usersQuery.data?.data ?? [],
      countsById,
      isCountsError,
    ),
    isPending: identitiesQuery.isPending || usersQuery.isLoading,
    isError: identitiesQuery.isError,
  };
};
